import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useBodyScrollLock } from '../lib/bodyScrollLock';
import './SearchBar.css';

const CYPRUS_CITIES = [
  'Nicosia', 'Limassol', 'Larnaca', 'Paphos',
  'Ayia Napa', 'Protaras', 'Paralimni',
];

const BEDROOM_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
];

const MIN_PRICE_BUY = [
  { value: '',        label: 'No min' },
  { value: '50000',   label: '€50k' },
  { value: '100000',  label: '€100k' },
  { value: '200000',  label: '€200k' },
  { value: '350000',  label: '€350k' },
  { value: '500000',  label: '€500k' },
  { value: '1000000', label: '€1M' },
];

const MAX_PRICE_BUY = [
  { value: '',        label: 'No max' },
  { value: '100000',  label: '€100k' },
  { value: '200000',  label: '€200k' },
  { value: '350000',  label: '€350k' },
  { value: '500000',  label: '€500k' },
  { value: '1000000', label: '€1M' },
  { value: '2000000', label: '€2M' },
];

const MIN_PRICE_RENT = [
  { value: '',     label: 'No min' },
  { value: '300',  label: '€300/mo' },
  { value: '500',  label: '€500/mo' },
  { value: '1000', label: '€1000/mo' },
  { value: '2000', label: '€2000/mo' },
];

const MAX_PRICE_RENT = [
  { value: '',     label: 'No max' },
  { value: '500',  label: '€500/mo' },
  { value: '1000', label: '€1000/mo' },
  { value: '2000', label: '€2000/mo' },
  { value: '5000', label: '€5000/mo' },
];

export default function SearchBar({ variant = 'hero' }) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [transactionType, setTransactionType] = useState('buy');
  const [city, setCity] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Lock body scroll while the mobile filter sheet is open. Shared lock — see
  // src/lib/bodyScrollLock.js for why we can't just save+restore here.
  useBodyScrollLock(showMobileFilters);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (transactionType) params.set('type', transactionType);
    if (city) params.set('city', city);
    if (propertyType) params.set('propertyType', propertyType);
    if (bedrooms) params.set('beds', bedrooms);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    const qs = params.toString();
    navigate(`/listings${qs ? `?${qs}` : ''}`);
    setShowMobileFilters(false);
  };

  const clearAll = () => {
    setTransactionType('buy');
    setCity('');
    setPropertyType('');
    setBedrooms('');
    setMinPrice('');
    setMaxPrice('');
  };

  const activeFilterCount = [city, propertyType, bedrooms, minPrice, maxPrice].filter(Boolean).length;
  const minPricePresets = transactionType === 'rent' ? MIN_PRICE_RENT : MIN_PRICE_BUY;
  const maxPricePresets = transactionType === 'rent' ? MAX_PRICE_RENT : MAX_PRICE_BUY;

  const propertyTypeOptions = [
    { value: '',                    label: t('home.search.propertyType') },
    { value: 'apartment',           label: t('home.search.types.apartment') },
    { value: 'house',               label: t('home.search.types.house') },
    { value: 'villa',               label: t('home.search.types.villa') },
    { value: 'commercial',          label: t('home.search.types.commercial') },
    { value: 'under-construction',  label: t('home.search.types.underConstruction') },
  ];

  // Portaled to document.body so position:fixed/inset:0 anchors to the real
  // viewport. If left inline, the Hero's .animate-fade-up wrapper keeps a
  // residual transform after its slide-up animation, which makes that wrapper
  // the containing block for fixed descendants — so the sheet would render
  // anchored to a small card with the top fields scrolled off.
  const mobileSheet = showMobileFilters && (
    <div className="searchBar__mobileSheet" onClick={() => setShowMobileFilters(false)}>
      <div className="searchBar__mobileSheetContent" onClick={(e) => e.stopPropagation()}>
        <div className="searchBar__mobileSheetHeader">
          <h3>Filters</h3>
          <button
            type="button"
            className="searchBar__mobileSheetClose"
            onClick={() => setShowMobileFilters(false)}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="searchBar__mobileSheetBody">
          <div className="searchBar__mobileField">
            <label>{t('home.search.buy')} / {t('home.search.rent')}</label>
            <div className="searchBar__typeToggle searchBar__typeToggle--mobile">
              <button
                type="button"
                className={`searchBar__typeBtn ${transactionType === 'buy' ? 'searchBar__typeBtn--active' : ''}`}
                onClick={() => setTransactionType('buy')}
              >{t('home.search.buy')}</button>
              <button
                type="button"
                className={`searchBar__typeBtn ${transactionType === 'rent' ? 'searchBar__typeBtn--active' : ''}`}
                onClick={() => setTransactionType('rent')}
              >{t('home.search.rent')}</button>
            </div>
          </div>

          <div className="searchBar__mobileField">
            <label>Location</label>
            <select value={city} onChange={(e) => setCity(e.target.value)} className="searchBar__select">
              <option value="">Any city</option>
              {CYPRUS_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="searchBar__mobileField">
            <label>{t('home.search.propertyType')}</label>
            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className="searchBar__select">
              {propertyTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          <div className="searchBar__mobileField">
            <label>Bedrooms</label>
            <select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} className="searchBar__select">
              {BEDROOM_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          <div className="searchBar__mobileField">
            <label>Min price</label>
            <select value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="searchBar__select">
              {minPricePresets.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          <div className="searchBar__mobileField">
            <label>Max price</label>
            <select value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="searchBar__select">
              {maxPricePresets.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>

        <div className="searchBar__mobileSheetFooter">
          <button type="button" className="searchBar__clearBtn" onClick={clearAll}>
            Clear all
          </button>
          <button type="button" className="searchBar__applyBtn" onClick={handleSubmit}>
            {t('home.search.button')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`searchBar searchBar--${variant}`}>
      <div className="searchBar__typeToggle">
        <button
          type="button"
          className={`searchBar__typeBtn ${transactionType === 'buy' ? 'searchBar__typeBtn--active' : ''}`}
          onClick={() => setTransactionType('buy')}
        >
          {t('home.search.buy')}
        </button>
        <button
          type="button"
          className={`searchBar__typeBtn ${transactionType === 'rent' ? 'searchBar__typeBtn--active' : ''}`}
          onClick={() => setTransactionType('rent')}
        >
          {t('home.search.rent')}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="searchBar__form">
        <div className="searchBar__field searchBar__field--city">
          <label className="searchBar__label">Location</label>
          <select
            className="searchBar__select"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            aria-label="Location"
          >
            <option value="">Any city in Cyprus</option>
            {CYPRUS_CITIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="searchBar__field searchBar__field--type">
          <label className="searchBar__label">Property</label>
          <select
            className="searchBar__select"
            value={propertyType}
            onChange={(e) => setPropertyType(e.target.value)}
            aria-label="Property type"
          >
            {propertyTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="searchBar__field searchBar__field--beds">
          <label className="searchBar__label">Bedrooms</label>
          <select
            className="searchBar__select"
            value={bedrooms}
            onChange={(e) => setBedrooms(e.target.value)}
            aria-label="Bedrooms"
          >
            {BEDROOM_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="searchBar__field searchBar__field--price">
          <label className="searchBar__label">Min Price</label>
          <select
            className="searchBar__select"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            aria-label="Min price"
          >
            {minPricePresets.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="searchBar__field searchBar__field--price">
          <label className="searchBar__label">Max Price</label>
          <select
            className="searchBar__select"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            aria-label="Max price"
          >
            {maxPricePresets.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <button type="submit" className="searchBar__submit" aria-label={t('home.search.button')}>
          <Search size={18} />
          <span className="searchBar__submitLabel">{t('home.search.button')}</span>
        </button>
      </form>

      <button
        type="button"
        className="searchBar__mobileFiltersBtn"
        onClick={() => setShowMobileFilters(true)}
        aria-label="More filters"
      >
        <span>More filters</span>
        {activeFilterCount > 0 && (
          <span className="searchBar__activeCount">{activeFilterCount}</span>
        )}
      </button>

      {mobileSheet && createPortal(mobileSheet, document.body)}
    </div>
  );
}
